import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import { LogoNexus } from './LogoNexus.tsx';
import { CommandPalette, useAtajoBuscar } from './CommandPalette.tsx';
import { NotificationBell } from './NotificationBell.tsx';
import { ROUTES, type RouteDef } from '../lib/routes.ts';
import { navigate } from '../lib/router.ts';
import type { Sesion } from '../adapters/http.ts';
import type { DataPort } from '../adapters/port.ts';

/**
 * Shell portado del export «TOPS NEXUS Operating System» de Martín
 * (ui_kits/nexus-app · Sidebar.jsx, Topbar.jsx, Primitives.jsx).
 *
 * Medidas y colores tomados TEXTUALES de su bundle, no estimados:
 *   riel 264px sobre --ds-rail (#0f1a52) · topbar 64px con blur(12px)
 *   · ítem activo con barra roja interior de 3px · tarjetas radio 18
 *
 * El diseño reemplaza la barra de tabs por un riel lateral agrupado. Ganancia
 * real: las doce vistas quedan visibles a la vez, sin esconder ninguna detrás
 * del buscador —que es justo el defecto por el que Entregables no se encontraba—.
 */

/** Los grupos del riel, en el orden del flujo. `null` = grupo de cabecera. */
const GRUPOS: ReadonlyArray<string | null> = [null, 'Flujo creativo', 'Explorar', 'Ayuda'];

export function AppShell({
  route, sourceLabel, sesion, onSalir, port, children,
}: {
  route: RouteDef | null;
  sourceLabel: string;
  sesion?: Sesion | undefined;
  onSalir?: (() => void) | undefined;
  port?: DataPort | undefined;
  children: ReactNode;
}) {
  const real = sourceLabel.startsWith('DATOS REALES');
  const [buscando, setBuscando] = useState(false);
  useAtajoBuscar(useCallback(() => setBuscando(true), []));

  /**
   * El riel como cajón en pantallas chicas.
   *
   * Existe porque el porte del diseño lo mandó fuera de pantalla con
   * `translateX(-100%)` sin dejar NADA que lo trajera de vuelta: en un teléfono
   * las doce secciones quedaban inalcanzables. Lo encontró Dirección entrando
   * desde el celular, no una prueba.
   */
  const [menu, setMenu] = useState(false);
  const cerrar = useCallback(() => setMenu(false), []);

  // Escape cierra el cajón. Si algo se abre tapando la pantalla, tiene que
  // haber una salida de teclado; si no, el usuario queda encerrado.
  useEffect(() => {
    if (!menu) return;
    const alTeclear = (e: KeyboardEvent) => { if (e.key === 'Escape') cerrar(); };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [menu, cerrar]);

  return (
    <div className="ds-shell">
      <CommandPalette abierto={buscando} onCerrar={() => setBuscando(false)} />

      {/* El velo sólo existe con el cajón abierto: tocar afuera cierra, que es
          lo que todo el mundo intenta primero. */}
      {menu && <div className="ds-velo" onClick={cerrar} aria-hidden="true" />}

      {/* ── Riel lateral ─────────────────────────────────────────────────── */}
      <aside className={'ds-rail' + (menu ? ' ds-rail--abierto' : '')} aria-label="Navegación principal">
        <div className="ds-rail__brand">
          <LogoNexus size={72} />
          <div className="ds-rail__name">NEXUS CREATIVE</div>
          <div className="ds-rail__sub">PLATFORM</div>
        </div>

        <nav className="ds-rail__nav">
          {GRUPOS.map((g) => {
            const items = ROUTES.filter((r) => (r.grupo ?? null) === g);
            if (items.length === 0) return null;
            return (
              <div className="ds-rail__group" key={g ?? 'principal'}>
                {g && <div className="ds-rail__glabel">{g}</div>}
                {items.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className={'ds-navitem' + (r.id === route?.id ? ' ds-navitem--active' : '')}
                    onClick={() => { navigate(r.path); cerrar(); }}
                    aria-current={r.id === route?.id ? 'page' : undefined}
                  >
                    <span className="ds-navitem__icon">
                      <Icon name={r.icono ?? 'layout-grid'} size={18} />
                    </span>
                    <span className="ds-navitem__label">{r.label}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="ds-rail__foot">
          <span className="ds-dot ds-dot--ok" />
          <span>Todos los guardianes activos</span>
        </div>
      </aside>

      {/* ── Columna principal ────────────────────────────────────────────── */}
      <div className="ds-main">
        <header className="ds-topbar">
          {/* Sólo aparece en pantallas chicas: en escritorio el riel ya está visible. */}
          <button type="button" className="ds-menu" onClick={() => setMenu((v) => !v)}
                  aria-label={menu ? 'Cerrar el menú' : 'Abrir el menú de secciones'}
                  aria-expanded={menu}>
            <Icon name={menu ? 'x' : 'menu'} size={20} />
          </button>

          <div className="ds-topbar__brand">
            <span className="ds-topbar__name">NEXUS CREATIVE</span>
            <span className="ds-topbar__tag">Platform</span>
          </div>

          <button type="button" className="ds-search" onClick={() => setBuscando(true)}
                  aria-label="Buscar en el sistema">
            <Icon name="search" size={16} />
            <span>Buscar en el sistema…</span>
            <kbd>⌘K</kbd>
          </button>

          <div className="ds-topbar__right">
            {port && sesion && <NotificationBell port={port} />}
            {sesion && (
              <>
                <span className="ds-user">{sesion.displayName}</span>
                <span className="ds-role">{sesion.role}</span>
                {onSalir && <button type="button" className="ds-btn-ghost" onClick={onSalir}>Salir</button>}
              </>
            )}
          </div>
        </header>

        <main className="ds-content">
          {route && (
            <div className="ds-head">
              <div className="ds-head__left">
                <div className="ds-eyebrow">{route.eyebrow}</div>
                <h1 className="ds-title">{route.title}</h1>
              </div>
              <div className="ds-head__right">
                <span className="ds-live">
                  <span className="ds-dot ds-dot--blue ds-dot--pulse" />
                  Estudio activo
                </span>
                <span className={'ds-chip ' + (real ? 'ds-chip--ok' : 'ds-chip--warn')} data-testid="source-label">
                  {sourceLabel}
                </span>
              </div>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
